import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User } from '@/types/user'
import { authProvider } from '@/services/currentAuthProvider'
import { REDIRECT_IN_PROGRESS } from '@/services/AuthProvider'
import { useUserProfileStore } from '@/stores/userProfile'
import { useMfaStore } from '@/stores/mfa'
import { handleSessionExpired, isSessionExpiredError, refreshIdToken } from '@/services/sessionExpiry'
import { getGlobalT } from '@/i18n'
import { initApiService } from '@/services/init'
import { initLocaleFromStorage, initLocaleFromProfile } from '@/composables/useLocale'
import { resetClientAvatarCache } from '@/composables/useClientAvatar'
import { getAppMode } from '@/utils/domain'
import router from '@/router'
import { extractApiErrorMessage, logUnexpectedFailure } from '@/utils/errorHandling'
import { getSignInErrorMessage } from '@/utils/signInErrorMessage'
import { takePendingInvitation } from '@/services/pendingInvitation'
import { useKeycloakSignIn } from '@/composables/useKeycloakSignIn'
import { forgetAgreementPromptDismissal } from '@/services/agreementPromptDismissal'

export const useAuthStore = defineStore('auth', () => {
  const t = getGlobalT()

  const user = ref<User | null>(null)
  const loading = ref(true)
  const error = ref<string | null>(null)

  const mfaService = authProvider.mfa
  const { redirectSsoIdentityToKeycloakIfSwitched } = useKeycloakSignIn()

  // A user returning from the mobile SSO redirect is already set when the listener first fires, so
  // `previousUser` alone would read their sign-in as a page refresh and skip the profile and
  // permissions fetch below.
  let signedInByRedirect = false
  // An SSO sign-in leaves Firebase signed in before anyone knows whether this person belongs on
  // Keycloak, so the listener must not start a session on it. Staying on Firebase applies the user
  // itself; leaving for Keycloak ends the page.
  let decidingSsoSignIn = false

  const isAuthenticated = computed(() => user.value !== null)

  // Initialize auth state listener
  async function initAuthListener() {
    // Handle redirect result FIRST and AWAIT it before setting up the listener
    // This is critical for mobile SSO flow - we must know the redirect result
    // before onAuthStateChanged fires, otherwise we get a race condition where
    // onAuthStateChanged fires with null before getRedirectResult() completes
    try {
      if (await readSsoRedirectResult()) return
    } catch (err) {
      logUnexpectedFailure('Failed to handle redirect result:', err)
      error.value = getSignInErrorMessage(err, t, t('errors.completeSignIn'))
      loading.value = false
    }

    authProvider.onAuthStateChanged(async (newUser) => {
      if (!decidingSsoSignIn) await applyAuthState(newUser)
    })
  }

  /** Finishes a mobile SSO sign-in returning through a page reload; `true` when it is leaving for Keycloak. */
  async function readSsoRedirectResult(): Promise<boolean> {
    try {
      const redirectUser = await authProvider.handleRedirectResult()
      if (redirectUser) {
        if (await redirectSsoIdentityToKeycloakIfSwitched(redirectUser)) return true
        user.value = redirectUser
        signedInByRedirect = true
        // Don't set loading=false here - let onAuthStateChanged handle it
        // after fetching the profile
      }
      return false
    } catch (err) {
      if (!mfaService.isMfaRequiredError(err)) throw err
      // An MFA-enrolled user completing the mobile SSO redirect needs the MFA
      // challenge, not an error. Mirror the popup flow handled in signInWithSSO().
      if (await redirectSsoIdentityToKeycloakIfSwitched(mfaService.getMfaRequiredIdentity(err))) return true
      const mfaStore = useMfaStore()
      mfaStore.setChallenge(mfaService.getMfaResolver(err))
      // The behandelaar app shows the challenge on a dedicated route; the admin
      // portal renders it reactively from the store, so it needs no navigation.
      if (getAppMode() === 'behandelaar') {
        router.push({ name: 'mfa-challenge' })
      }
      loading.value = false
      return false
    }
  }

  async function applyAuthState(newUser: User | null) {
    const previousUser = signedInByRedirect ? null : user.value
    signedInByRedirect = false
    user.value = newUser

    // Manage user profile store based on auth state changes
    const userProfileStore = useUserProfileStore()

    if (newUser && !previousUser) {
      // User just logged in - fetch their profile BEFORE setting loading=false
      // This ensures the profile (including organizationId) is available before
      // the app navigates to authenticated routes like /dashboard
      // This triggers processSignIn() on the backend via the auth filter,
      // which sets Firebase custom claims (organizationId); the profile store
      // reads the user's permissions from the API alongside the profile
      try {
        // Force token refresh BEFORE any API call to ensure we have valid credentials
        // This prevents 401 errors that occur when the token is stale or not yet ready
        // during auth state transitions (Fixes SCRIVIO-FE-3D)
        await refreshIdToken()
        // Only fetch user profile for behandelaar app - admin and portal use different auth flows
        // Admin portal uses useAdminAuth composable, client portal uses /api/portal/me endpoint
        const appMode = getAppMode()
        if (appMode === 'behandelaar') {
          await userProfileStore.fetchProfile()
          // Initialize locale from user profile after fetch
          // Use standalone function instead of useLocale() which requires Vue setup context
          await initLocaleFromProfile(userProfileStore.profile?.language)
        } else if (appMode === 'client') {
          // The portal speaks the language the behandelaar set on the cliënt's dossier
          const { data: clientProfile } = await initApiService().portal.getCurrentClientProfile()
          await initLocaleFromProfile(clientProfile.language)
        }
      } catch (err) {
        // A session restored from a dead refresh token surfaces here too; end it
        // quietly rather than logging it as a failure (Fixes SCRIVIO-FE-8T).
        if (isSessionExpiredError(err)) {
          loading.value = false
          await handleSessionExpired()
          return
        }
        logUnexpectedFailure('Failed to fetch profile after login:', err)
      }

      // Force token refresh to pick up custom claims set by the backend
      await refreshIdToken()
      user.value = await authProvider.getCurrentUser()

      // Auto-accept pending invitation if present
      const pendingToken = takePendingInvitation()
      if (pendingToken) {
        await redeemPendingInvitation(pendingToken)
      }

      // Now that profile is loaded, allow navigation to proceed
      loading.value = false
    } else if (!newUser && previousUser) {
      // User just logged out - clear their profile
      userProfileStore.clearProfile()
      // An agreement prompt put off with "Later" is asked again at the next sign-in (#4494)
      forgetAgreementPromptDismissal()
      loading.value = false
    } else if (!newUser && !previousUser) {
      // Initial load with no authenticated user - restore locale from localStorage
      // Use standalone function instead of useLocale() which requires Vue setup context
      await initLocaleFromStorage()
      loading.value = false
    } else {
      // User already logged in (page refresh) - loading already false from redirect handler
      loading.value = false
    }
  }

  /**
   * Replay the invitation token parked before the visitor was sent to the login
   * screen, then put the session on the claims the invitation produced.
   *
   * The accept call is best-effort by design. `UserSignInService.handleFirstTimeUser`
   * already applies a pending invitation matched on the verified email during the
   * very first authenticated request, so for the common "register through the
   * invitation link" flow the invitation is `ACCEPTED` before this replay runs and
   * the accept answers `410 Gone`. That is the expected outcome, not a fault — the
   * membership and role are in place. The replay still matters for someone who had
   * signed in before, because the returning-user path never looks at invitations.
   *
   * An invitation that needs an agreement acceptance (#4491) is the one sign-in
   * leaves pending, and this replay carries none, so it answers `400`. That too is
   * expected: the user is left without an organization and is sent to the
   * invitation page below, which shows the checkbox.
   *
   * Refreshing the session therefore happens regardless of how the accept ended:
   * whoever applied the invitation did so after the token in hand was minted, so
   * without it the session runs on pre-invitation custom claims until the next
   * natural refresh — which is what made `/signals/visible-to-me` answer 403 right
   * after onboarding (#3259).
   */
  async function redeemPendingInvitation(token: string): Promise<void> {
    const userProfileStore = useUserProfileStore()

    try {
      await initApiService().invitations.acceptInvitation(token)
    } catch (err) {
      // A 4xx means the token can no longer be consumed — already accepted,
      // expired or revoked. Whether that left the user without a membership is
      // decided below by the refreshed profile, so it must not be reported as a
      // failure here (#3254, #3143).
      logUnexpectedFailure('Failed to accept pending invitation:', err)
    }

    try {
      await refreshIdToken()
      user.value = await authProvider.getCurrentUser()
      await userProfileStore.fetchProfile()
    } catch (err) {
      // Without a fresh profile there is nothing to judge the outcome on, so
      // leave the user where they are rather than guessing. A session that died
      // mid-flow is not a fault worth reporting either (SCRIVIO-FE-8T).
      if (!isSessionExpiredError(err)) {
        logUnexpectedFailure('Failed to refresh session after accepting invitation:', err)
      }
      return
    }

    // No organization means nobody applied the invitation: no membership, no
    // role, and a dashboard that would be an empty shell with no explanation.
    // Send the user back to the invitation page, which names the reason
    // (expired / revoked / not found) or offers the accept again.
    if (!userProfileStore.profile?.organizationId) {
      router.push({ name: 'accept-invitation', params: { token } })
    }
  }

  async function signIn(email: string, password: string): Promise<{ mfaRequired: boolean }> {
    try {
      loading.value = true
      error.value = null
      user.value = await authProvider.signIn(email, password)
      return { mfaRequired: false }
    } catch (e: unknown) {
      // Check if MFA is required
      if (mfaService.isMfaRequiredError(e)) {
        const mfaStore = useMfaStore()
        const resolver = mfaService.getMfaResolver(e)
        mfaStore.setChallenge(resolver)
        return { mfaRequired: true }
      }
      error.value = extractApiErrorMessage(e, t('errors.signInFailed'))
      throw e
    } finally {
      loading.value = false
    }
  }

  async function signUp(email: string, password: string) {
    try {
      loading.value = true
      error.value = null
      user.value = await authProvider.signUp(email, password)
    } catch (e: unknown) {
      error.value = extractApiErrorMessage(e, t('errors.createAccount'))
      throw e
    } finally {
      loading.value = false
    }
  }

  async function signInWithSSO(provider: string): Promise<{ mfaRequired: boolean; redirected: boolean }> {
    let leavingForKeycloak = false
    try {
      loading.value = true
      error.value = null
      decidingSsoSignIn = true
      const ssoUser = await authProvider.signInWithSSO(provider)
      leavingForKeycloak = await redirectSsoIdentityToKeycloakIfSwitched({
        email: ssoUser.email,
        identityProvider: provider,
      })
      if (!leavingForKeycloak) void applyAuthState(ssoUser)
      return { mfaRequired: false, redirected: leavingForKeycloak }
    } catch (e: unknown) {
      // Handle redirect in progress (mobile flow)
      if (e instanceof Error && e.message === REDIRECT_IN_PROGRESS) {
        // Don't set loading to false or clear error - redirect is happening
        return { mfaRequired: false, redirected: true }
      }
      // Check if MFA is required
      if (mfaService.isMfaRequiredError(e)) {
        leavingForKeycloak = await redirectSsoIdentityToKeycloakIfSwitched({
          ...mfaService.getMfaRequiredIdentity(e),
          identityProvider: provider,
        })
        if (leavingForKeycloak) return { mfaRequired: false, redirected: true }
        const mfaStore = useMfaStore()
        const resolver = mfaService.getMfaResolver(e)
        mfaStore.setChallenge(resolver)
        return { mfaRequired: true, redirected: false }
      }
      error.value = extractApiErrorMessage(e, t('errors.signInSsoFailed'))
      throw e
    } finally {
      decidingSsoSignIn = leavingForKeycloak
      loading.value = false
    }
  }

  async function signOut() {
    try {
      loading.value = true
      error.value = null
      await authProvider.signOut()
      user.value = null
      resetClientAvatarCache()
    } catch (e: unknown) {
      error.value = extractApiErrorMessage(e, t('errors.signOut'))
      throw e
    } finally {
      loading.value = false
    }
  }

  async function getIdToken(): Promise<string | null> {
    return await authProvider.getIdToken()
  }

  async function refreshUser() {
    try {
      loading.value = true
      error.value = null
      user.value = await authProvider.getCurrentUser()
    } catch (e: unknown) {
      error.value = extractApiErrorMessage(e, t('errors.refreshUser'))
      throw e
    } finally {
      loading.value = false
    }
  }

  return {
    user,
    loading,
    error,
    isAuthenticated,
    initAuthListener,
    signIn,
    signUp,
    signInWithSSO,
    signOut,
    getIdToken,
    refreshUser,
  }
})
